import { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Headphones, MessageSquare, Users, LogOut, LayoutDashboard } from 'lucide-react';
import { logger } from '../utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SupportLayout() {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/support/me`, { credentials: 'include' });
      if (!res.ok) throw new Error('Not authenticated');
      const data = await res.json();
      setAgent(data);
    } catch (err) { logger.error("Operation failed:", err);
      navigate('/support/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const handleLogout = async () => {
    await fetch(`${API}/api/support/logout`, { method: 'POST', credentials: 'include' });
    localStorage.removeItem('supportAgent');
    navigate('/support/login');
  };

  if (loading) {
    return <div className="min-h-screen bg-[#060C18] flex items-center justify-center"><div className="text-white/50">Loading...</div></div>;
  }

  const navItems = [
    { path: '/support', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/support/tickets', icon: MessageSquare, label: 'Tickets' },
    { path: '/support/users', icon: Users, label: 'Users' },
  ];

  return (
    <div data-testid="support-layout" className="min-h-screen bg-[#060C18] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0A1222] border-r border-white/10 flex flex-col shrink-0">
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center">
              <Headphones className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm">Support Portal</p>
              <p className="text-white/40 text-xs">{agent?.email}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(item => {
            const isActive = item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? 'bg-emerald-500/10 text-emerald-400' : 'text-white/50 hover:text-white hover:bg-white/5'}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10">
          <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/50 hover:text-white hover:bg-white/5 w-full">
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto p-8">
        <Outlet context={{ agent }} />
      </main>
    </div>
  );
}
